import { useMemo } from 'react';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import type {
  WorkflowAccessReport,
  WorkflowCatalogEntry,
  WorkflowRunDetail,
  WorkflowRunState,
  WorkflowRunSummary,
} from '../../../types/backend';

// Workflow runs (ADR-0055). ResolveWorkflowGate is here and NOT among the agent's tools: an
// approval is a human act, and the wall is what enforces that rather than a prompt.
export const createWorkflowRunService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    listRunnable: async (customerId: string): Promise<WorkflowCatalogEntry[]> =>
      rpcCall<WorkflowCatalogEntry[]>(client, 'investigations.ListRunnableWorkflows', {
        customer_id: customerId,
      }),

    listRuns: async (
      customerId: string,
      investigationId: string,
    ): Promise<WorkflowRunSummary[]> =>
      rpcCall<WorkflowRunSummary[]>(client, 'investigations.ListWorkflowRuns', {
        customer_id: customerId,
        investigation_id: investigationId,
      }),

    // Every run on the customer, including the ones no investigation shows. The backend takes
    // the customer from the verified context and ignores the field; it is sent because the
    // authorization wall reads it.
    listCustomerRuns: async (customerId: string): Promise<WorkflowRunSummary[]> =>
      rpcCall<WorkflowRunSummary[]>(client, 'investigations.ListCustomerWorkflowRuns', {
        customer_id: customerId,
      }),

    // One run addressed by the customer alone. The backend's ref request takes the investigation
    // as optional and reads the run out of the customer partition (ADR-0057), so a run no case
    // links is readable here; the sidebar's getRun passes one only because it has one.
    getCustomerRun: async (
      customerId: string,
      runId: string,
    ): Promise<WorkflowRunDetail> =>
      rpcCall<WorkflowRunDetail>(client, 'investigations.GetWorkflowRun', {
        customer_id: customerId,
        run_id: runId,
      }),

    getRun: async (
      customerId: string,
      investigationId: string,
      runId: string,
    ): Promise<WorkflowRunDetail> =>
      rpcCall<WorkflowRunDetail>(client, 'investigations.GetWorkflowRun', {
        customer_id: customerId,
        investigation_id: investigationId,
        run_id: runId,
      }),

    // What each step of a workflow needs against the caller's access, before anything starts.
    checkAccess: async (
      customerId: string,
      investigationId: string,
      workflowName: string,
    ): Promise<WorkflowAccessReport> =>
      rpcCall<WorkflowAccessReport>(client, 'investigations.CheckWorkflowAccess', {
        customer_id: customerId,
        investigation_id: investigationId,
        workflow_name: workflowName,
      }),

    startWorkflow: async (
      customerId: string,
      investigationId: string,
      workflowName: string,
    ): Promise<WorkflowRunState> =>
      rpcCall<WorkflowRunState>(client, 'investigations.StartWorkflow', {
        customer_id: customerId,
        investigation_id: investigationId,
        workflow_name: workflowName,
      }),

    resolveGate: async (
      customerId: string,
      investigationId: string,
      runId: string,
      nodeId: string,
      approved: boolean,
      note?: string,
    ): Promise<unknown> =>
      rpcCall(client, 'investigations.ResolveWorkflowGate', {
        customer_id: customerId,
        investigation_id: investigationId,
        run_id: runId,
        node_id: nodeId,
        approved,
        ...(note ? { note } : {}),
      }),

    // The operator answering a decision themselves. The agent can take a branch from its own
    // tool, so this is the same write from the other side, never a second way to route a run.
    takeBranch: async (
      customerId: string,
      investigationId: string,
      runId: string,
      nodeId: string,
      condition: string,
      reason: string,
    ): Promise<unknown> =>
      rpcCall(client, 'investigations.TakeBranch', {
        customer_id: customerId,
        investigation_id: investigationId,
        run_id: runId,
        node_id: nodeId,
        condition,
        reason,
      }),

    // Both are human acts, like a gate answer: the agent can report a step failed but only an
    // operator sends it back or stops the run.
    retryStep: async (
      customerId: string,
      investigationId: string,
      runId: string,
      nodeId: string,
    ): Promise<unknown> =>
      rpcCall(client, 'investigations.RetryWorkflowStep', {
        customer_id: customerId,
        investigation_id: investigationId,
        run_id: runId,
        node_id: nodeId,
      }),

    abandonRun: async (
      customerId: string,
      investigationId: string,
      runId: string,
      reason: string,
    ): Promise<unknown> =>
      rpcCall(client, 'investigations.AbandonWorkflowRun', {
        customer_id: customerId,
        investigation_id: investigationId,
        run_id: runId,
        reason,
      }),
  };
};

export const useWorkflowRunService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('useWorkflowRunService requires authentication');
    }

    return createWorkflowRunService(idToken);
  }, [idToken]);
};
